import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Clock } from 'lucide-react';
import type { PostMeta } from '@/blog/types';
import { TableOfContents } from './TableOfContents';

interface BlogLayoutProps {
  meta: PostMeta;
  prev: PostMeta | null;
  next: PostMeta | null;
  children: React.ReactNode;
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function BlogLayout({ meta, prev, next, children }: BlogLayoutProps) {
  const articleRef = useRef<HTMLElement>(null);

  return (
    <div className="min-h-full bg-background px-4 py-12">
      <div className="mx-auto max-w-6xl">
        {/* Back link */}
        <Link
          to="/blog"
          className="mb-8 inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          All posts
        </Link>

        <div className="grid grid-cols-1 gap-12 xl:grid-cols-[1fr_200px]">
          {/* ── Left column: header + article + post nav ───────────────── */}
          <div className="min-w-0">
            {/* Post header */}
            <header className="mb-10 mt-4">
              <h1 className="mb-3 text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
                {meta.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <time dateTime={meta.date}>{formatDate(meta.date)}</time>
                {meta.readingTime != null && (
                  <>
                    <span aria-hidden>·</span>
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" aria-hidden />
                      {meta.readingTime} min read
                    </span>
                  </>
                )}
                {meta.tags && meta.tags.length > 0 && (
                  <>
                    <span aria-hidden>·</span>
                    <div className="flex flex-wrap gap-1.5">
                      {meta.tags.map((tag) => (
                        <span
                          key={tag}
                          className="rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </header>

            {/* MDX content */}
            <article
              ref={articleRef}
              className="prose prose-neutral max-w-none dark:prose-invert prose-headings:font-semibold prose-headings:tracking-tight prose-headings:text-foreground prose-p:text-foreground/90 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-primary prose-blockquote:text-muted-foreground prose-strong:text-foreground prose-code:rounded prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:text-sm prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-pre:rounded-lg prose-pre:border prose-pre:border-border/40 prose-pre:bg-transparent prose-pre:p-0 prose-li:text-foreground/90 prose-hr:border-border"
            >
              {children}
            </article>

            {/* ── Post navigation ────────────────────────────────────── */}
            {(prev || next) && (
              <nav
                aria-label="Post navigation"
                className="mt-16 border-t border-border pt-8"
              >
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {/* Previous = older post (left slot) */}
                  {prev ? (
                    <Link
                      to={`/blog/${prev.slug}`}
                      className="group flex flex-col gap-1 rounded-lg border border-border bg-card p-4 transition-colors hover:border-primary/50 hover:bg-card/80"
                    >
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <ArrowLeft className="h-3 w-3 transition-transform group-hover:-translate-x-0.5" />
                        Previous
                      </span>
                      <span className="line-clamp-2 text-sm font-medium text-foreground group-hover:text-primary">
                        {prev.title}
                      </span>
                    </Link>
                  ) : (
                    <div /> /* empty cell keeps next pinned to the right */
                  )}

                  {/* Next = newer post (right slot) */}
                  {next && (
                    <Link
                      to={`/blog/${next.slug}`}
                      className="group flex flex-col items-end gap-1 rounded-lg border border-border bg-card p-4 text-right transition-colors hover:border-primary/50 hover:bg-card/80"
                    >
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        Next
                        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                      </span>
                      <span className="line-clamp-2 text-sm font-medium text-foreground group-hover:text-primary">
                        {next.title}
                      </span>
                    </Link>
                  )}
                </div>
              </nav>
            )}
          </div>

          {/* ── Right column: sticky ToC (hidden below xl) ─────────────── */}
          <aside className="hidden xl:flex xl:flex-col">
            <div className="sticky top-8 flex max-h-[calc(100dvh-4rem)] flex-col">
              <p className="mb-3 flex-none text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                On this page
              </p>
              <TableOfContents
                articleRef={articleRef}
                readingTime={meta.readingTime}
              />
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
